import { getWebSessionMarker } from './authSession';
import { authService } from './authService';
const WEB_REFRESH_LOCK_NAME = 'icavi:web-session-refresh';
const WEB_REFRESH_LEASE_KEY = '__icavi_web_refresh_lease';
const WEB_REFRESH_RESULT_KEY = '__icavi_web_refresh_result';
const WEB_REFRESH_LEASE_MS = 15000;
const WEB_REFRESH_WAIT_MS = 30000;
const WEB_REFRESH_POLL_MS = 50;
let refreshInFlight = null;
const isWebSessionPresent = () => Boolean(getWebSessionMarker());
const readRefreshResult = () => {
    try {
        const raw = localStorage.getItem(WEB_REFRESH_RESULT_KEY);
        if (!raw) {
            return null;
        }
        const result = JSON.parse(raw);
        if (!Number.isFinite(result.completedAt)) {
            return null;
        }
        return {
            completedAt: Number(result.completedAt),
            expiresIn: Number.isFinite(result.expiresIn) ? Number(result.expiresIn) : undefined,
        };
    }
    catch {
        return null;
    }
};
const publishRefreshResult = (response) => {
    try {
        localStorage.setItem(WEB_REFRESH_RESULT_KEY, JSON.stringify({
            completedAt: Date.now(),
            expiresIn: response.expires_in,
        }));
    }
    catch {
        // A memória local não deve impedir a renovação da sessão.
    }
};
const buildSharedSuccessResponse = (result) => ({
    status: 'success',
    ...(result.expiresIn ? { expires_in: result.expiresIn } : {}),
});
export const isSuccessfulWebSessionRefresh = (response) => {
    if (!response || typeof response !== 'object') {
        return false;
    }
    const successFlag = 'sucesso' in response && response.sucesso === true;
    return (!response.erro &&
        !response.error &&
        response.status !== 'erro' &&
        (successFlag || response.status === 'success' || response.status === 'sucesso'));
};
const readLease = () => {
    try {
        const raw = localStorage.getItem(WEB_REFRESH_LEASE_KEY);
        if (!raw) {
            return null;
        }
        const lease = JSON.parse(raw);
        if (typeof lease.owner !== 'string' || !Number.isFinite(lease.expiresAt)) {
            return null;
        }
        return {
            owner: lease.owner,
            expiresAt: Number(lease.expiresAt),
        };
    }
    catch {
        return null;
    }
};
const wait = (milliseconds) => new Promise(resolve => {
    window.setTimeout(resolve, milliseconds);
});
const runWithLocalStorageLease = async (startedAt, executeRefresh) => {
    const owner = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const deadline = Date.now() + WEB_REFRESH_WAIT_MS;
    while (Date.now() < deadline) {
        const recentResult = readRefreshResult();
        if (recentResult && recentResult.completedAt > startedAt && isWebSessionPresent()) {
            return buildSharedSuccessResponse(recentResult);
        }
        const now = Date.now();
        const currentLease = readLease();
        if (!currentLease || currentLease.expiresAt <= now) {
            try {
                localStorage.setItem(WEB_REFRESH_LEASE_KEY, JSON.stringify({ owner, expiresAt: now + WEB_REFRESH_LEASE_MS }));
            }
            catch {
                return executeRefresh();
            }
            if (readLease()?.owner === owner) {
                try {
                    const resultAfterAcquire = readRefreshResult();
                    if (resultAfterAcquire &&
                        resultAfterAcquire.completedAt > startedAt &&
                        isWebSessionPresent()) {
                        return buildSharedSuccessResponse(resultAfterAcquire);
                    }
                    return executeRefresh();
                }
                finally {
                    if (readLease()?.owner === owner) {
                        localStorage.removeItem(WEB_REFRESH_LEASE_KEY);
                    }
                }
            }
        }
        await wait(WEB_REFRESH_POLL_MS);
    }
    // The normal production path uses Web Locks. This fallback avoids blocking
    // the UI forever when localStorage coordination is unavailable or stale.
    return executeRefresh();
};
const runWithRefreshLock = async (startedAt, executeRefresh) => {
    const locks = navigator.locks;
    if (locks?.request) {
        return locks.request(WEB_REFRESH_LOCK_NAME, { mode: 'exclusive' }, async () => {
            const recentResult = readRefreshResult();
            if (recentResult && recentResult.completedAt > startedAt && isWebSessionPresent()) {
                return buildSharedSuccessResponse(recentResult);
            }
            return executeRefresh();
        });
    }
    return runWithLocalStorageLease(startedAt, executeRefresh);
};
export const refreshWebSession = async () => {
    if (!isWebSessionPresent()) {
        return null;
    }
    if (!refreshInFlight) {
        const startedAt = Date.now();
        refreshInFlight = runWithRefreshLock(startedAt, async () => {
            try {
                const response = await authService.refreshToken('');
                if (isSuccessfulWebSessionRefresh(response)) {
                    publishRefreshResult(response);
                }
                return response;
            }
            catch {
                return null;
            }
        }).finally(() => {
            refreshInFlight = null;
        });
    }
    return refreshInFlight;
};
